import { useState } from 'react';
import type { Dispatch } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DocumentCaptureZone } from '../../components/DocumentCaptureZone';
import { RetryMessage } from '../../components/RetryMessage';
import { DOC_TYPE } from '../../types/kyc';
import type { RejectionReason } from '../../constants/rejectionMessages';
import type { KYCFlowState, KYCAction } from '../KYCFlowController';
import { uploadDocument, executeWorkflow, KYCApiError } from '../../services/kycApi';
import { useSimulation } from '../../contexts/SimulationContext';

interface CapturePOAStepProps {
  state: KYCFlowState;
  dispatch: Dispatch<KYCAction>;
}

// Tipos de comprobante aceptados según reglas de negocio de Amarello
const ACCEPTED_POA_TYPES = [
  'Recibo de luz (CFE)',
  'Recibo de agua',
  'Recibo de gas',
  'Recibo de teléfono',
  'Estado de cuenta bancario',
];

// Umbrales de score para comprobante de domicilio según RFI de Amarello:
//   score >= 60 → APROBADO
//   score >= 40 y < 60 → APROBADO CONDICIONADO (alerta fraude, no rechazar)
//   score < 40 o documento vencido → RECHAZADO

// Paso de captura de comprobante de domicilio con validación de frescura ≤ 3 meses
export function CapturePOAStep({ state, dispatch }: CapturePOAStepProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);

  // Configuración de simulación — si está activa se omiten las llamadas al backend
  const simulation = useSimulation();

  const handleSubmit = async () => {
    // En modo simulación se omiten upload y workflow — el stream simulado emite el resultado
    if (simulation.enabled) {
      dispatch({ type: 'SUBMIT_POA' });
      return;
    }

    const file = selectedFile;
    if (!file) return;

    setProcessing(true);
    dispatch({ type: 'SET_ERROR', message: '' });

    try {
      // Paso 1: subir el comprobante al doc-handler
      const uploadResponse = await uploadDocument({
        file,
        sessionId: state.session.sessionId,
        token: state.session.token,
      });

      // Paso 2: transicionar a VALIDATING_POA — el SSE stream emitirá el resultado
      dispatch({ type: 'SUBMIT_POA' });

      // Paso 3: disparar el pipeline de clasificación en el backend
      await executeWorkflow({
        action: 'classification',
        sessionId: state.session.sessionId,
        token: state.session.token,
        // El GCP Workflow de clasificación espera process_id y filename (no file_id)
        payload: {
          process_id: uploadResponse.processId,
          filename: uploadResponse.filename,
          content_type: 'application/pdf',
        },
      });

      // A partir de aquí el KYCFlowController recibe el resultado via SSE
    } catch (err) {
      // Revertir a CAPTURING_POA en caso de error de red o upload fallido
      dispatch({ type: 'RETRY_POA' });
      const message =
        err instanceof KYCApiError
          ? err.message
          : 'Ocurrió un error al procesar tu comprobante. Intenta de nuevo.';
      dispatch({ type: 'SET_ERROR', message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Encabezado */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Comprobante de domicilio
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Necesitamos confirmar tu dirección actual. El comprobante debe ser reciente.
        </Typography>
      </Box>

      {/* Alerta de error si el backend falló */}
      {state.errorMessage && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {state.errorMessage}
        </Alert>
      )}

      {/* Indicador de frescura */}
      <Chip
        icon={<CalendarMonthIcon />}
        label="Debe tener fecha de los últimos 3 meses"
        color="info"
        size="small"
        variant="outlined"
        sx={{ alignSelf: 'flex-start' }}
      />

      {/* Indicador de reintento */}
      {state.poaRetries > 0 && (
        <Chip
          label={`Intento ${state.poaRetries + 1} de 3`}
          color="warning"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        />
      )}

      {/* Mensaje de instrucción si hubo rechazo previo */}
      {state.rejectionReason && state.poaRetries > 0 && (
        <RetryMessage reason={state.rejectionReason as RejectionReason} />
      )}

      {/* Tipos de documentos aceptados */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Documentos aceptados:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {ACCEPTED_POA_TYPES.map((type) => (
            <Chip key={type} label={type} size="small" variant="outlined" />
          ))}
        </Stack>
      </Box>

      {/* Zona de captura del comprobante */}
      <DocumentCaptureZone
        hasFile={selectedFile !== null}
        onFileSelected={setSelectedFile}
        dropHint="Arrastra tu comprobante aquí, o haz clic para seleccionar"
        cameraLabel="Coloca tu comprobante de domicilio dentro del marco"
        docVariant="poa"
      />

      {/* Botón de envío — muestra spinner mientras procesa */}
      <Button
        variant="contained"
        size="large"
        disabled={(!simulation.enabled && selectedFile === null) || processing}
        onClick={handleSubmit}
        fullWidth
        startIcon={processing ? <CircularProgress size={20} color="inherit" /> : undefined}
      >
        {processing ? 'Verificando...' : 'Subir comprobante'}
      </Button>
    </Box>
  );
}
